import React, { useEffect, useState } from 'react';
import { connectionsAPI } from '../../services/api';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const intervalLabel = (minutes) => {
  if (minutes === 1440) return 'Daily';
  if (minutes >= 60) return `Every ${minutes / 60} hour${minutes === 60 ? '' : 's'}`;
  return `Every ${minutes} minutes`;
};

/** Which tickets count towards a company's SLA rates, and how much history and how many API calls to allow. */
export default function HaloSettings({ connector, connection, companyId, onSaved }) {
  const [options, setOptions] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    connectionsAPI.options(connector.id, companyId)
      .then((res) => {
        if (cancelled) return;
        const s = connection.definitions || {};
        setOptions(res.data);
        setDraft({
          interval: connection.syncIntervalMinutes,
          slaTicketTypes: s.slaTicketTypes || [],
          slaTeams: s.slaTeams || [],
          historyMonths: s.historyMonths,
          monthlyCallBudget: s.monthlyCallBudget,
        });
      })
      .catch((err) => { if (!cancelled) setError(describeError(err, 'Could not read the options from Halo')); });
    return () => { cancelled = true; };
    // Options are read once when the settings open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, companyId]);

  if (error && !options) return <div className="error-message" role="alert">{error}</div>;
  if (!options || !draft) return <p className="source-hint">Reading the options from Halo…</p>;

  const set = (patch) => {
    setSaved(false);
    setDraft((d) => ({ ...d, ...patch }));
  };
  const toggle = (name, id) => set({ [name]: draft[name].includes(id) ? draft[name].filter((v) => v !== id) : [...draft[name], id] });

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const res = await connectionsAPI.saveSettings(connector.id, companyId, {
        syncIntervalMinutes: draft.interval,
        settings: {
          slaTicketTypes: draft.slaTicketTypes,
          slaTeams: draft.slaTeams,
          historyMonths: Number(draft.historyMonths),
          monthlyCallBudget: Number(draft.monthlyCallBudget),
        },
      });
      setSaved(true);
      onSaved(res.data);
    } catch (err) {
      setError(describeError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="connection-settings" onSubmit={save}>
      <label className="connection-field">
        <span>Sync frequency</span>
        <select value={draft.interval} onChange={(e) => set({ interval: Number(e.target.value) })}>
          {connector.syncIntervals.map((m) => <option key={m} value={m}>{intervalLabel(m)}</option>)}
        </select>
      </label>

      <fieldset className="connection-field">
        <legend>Ticket types that count towards SLA rates</legend>
        <p className="source-hint">Leave all unticked to include every type. Tickets excluded from SLA in Halo never count.</p>
        <div className="connection-checks">
          {options.ticketTypes.map((t) => (
            <label key={t.id}>
              <input type="checkbox" aria-label={`Ticket type ${t.name}`} checked={draft.slaTicketTypes.includes(t.id)} onChange={() => toggle('slaTicketTypes', t.id)} />
              {t.name}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="connection-field">
        <legend>Teams that count towards SLA rates</legend>
        <p className="source-hint">Leave all unticked to include every team.</p>
        <div className="connection-checks">
          {options.teams.map((t) => (
            <label key={t}>
              <input type="checkbox" aria-label={`Team ${t}`} checked={draft.slaTeams.includes(t)} onChange={() => toggle('slaTeams', t)} />
              {t}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="connection-field">
        <span>Months of history</span>
        <input type="number" min="1" max="120" step="1" value={draft.historyMonths} onChange={(e) => set({ historyMonths: e.target.value })} />
      </label>

      <label className="connection-field">
        <span>Monthly API call budget</span>
        <input type="number" min="100" max="1000000" step="100" value={draft.monthlyCallBudget} onChange={(e) => set({ monthlyCallBudget: e.target.value })} />
      </label>
      <p className="source-hint">Counts only calls made by the wallboard, not other integrations using Halo.</p>

      {error && <div className="error-message" role="alert">{error}</div>}
      <div className="connection-actions">
        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        {saved && <span className="source-hint connection-saved">Saved. A change to what is read starts a full sync.</span>}
      </div>
    </form>
  );
}
